import { Fragment, MouseEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isNil } from 'lodash'
import { useFeature } from '@growthbook/growthbook-react'

import { useNavigate } from 'features/i18n/router'
import { ROUTE_PATH } from 'lib/paths'
import { Activity, ACTIVITY_PAGE_TYPE, Group } from 'lib/_types'
import { isGroupActivityAlreadyAction } from '../helpers'
import { logEvent } from 'lib/analytics'
import { openApp } from 'lib/helpers'
import { useGetPostMutateMutation } from 'store/api/yay/post'
import { useGetGroupMutateMutation } from 'store/api/yay/group'
import {
  toggleDefaultConfirmBox,
  toggleGroupModeratorOfferConfirmBox,
  toggleGroupOwnerTransferConfirmBox,
} from 'store/actions/confirmBox'

import ConfirmBoxBasic from 'components/ConfirmBoxBasic'
import { ActivityListItem } from './ActivityListItem'

import './style.scss'

type Props = {
  activities: Activity[]
  pageType?: ACTIVITY_PAGE_TYPE
  enableViewportCheck?: boolean
  resizeTrigger?: number
}

export const ActivityList = ({
  activities,
  pageType,
  enableViewportCheck,
  resizeTrigger,
}: Props) => {
  const [isShowConfirmBox, setIsShowConfirmBox] = useState(false)
  const [confirmBoxContent, setConfirmBoxContent] = useState<{
    data: Activity
    title: string
    content: string
  }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const isEmplGiftsEnabled = useFeature('yay-17610')?.on

  const [getPostMutate] = useGetPostMutateMutation()
  const [getGroupMutate] = useGetGroupMutateMutation()

  const closeConfirmBox = () => {
    setIsShowConfirmBox(false)
  }

  const onOk = () => {
    switch (confirmBoxContent?.data.type) {
      // case 'wallet_daily_quests_completed':
      //   openApp()
      //   break

      default:
        break
    }
    closeConfirmBox()
  }

  const onClick = (activity: Activity) => async (e: MouseEvent) => {
    logEvent.activity_tap({ type: activity?.type })

    // any condition that not pass e.preventDefault() will be handle by link navigation in ActivityListItem
    switch (activity.type) {
      case 'id_check_checking':
      case 'id_check_success':
      case 'id_check_failed':
        e.preventDefault()
        setConfirmBoxContent({
          data: activity,
          title: t('activity_list.confirm_box.app.title'),
          content: t('activity_list.confirm_box.app.content'),
        })
        setIsShowConfirmBox(true)
        break

      case 'wallet_daily_quests_completed':
        e.preventDefault()
        setConfirmBoxContent({
          data: activity,
          title: t('activity_list.confirm_box.pal.title'),
          content: t('activity_list.confirm_box.pal.content'),
        })
        setIsShowConfirmBox(true)
        break

      case 'group_deputization_request':
        // https://jira.nanameue.jp/browse/YAY-8006
        if (
          !isGroupActivityAlreadyAction(
            activities,
            ['group_promote'],
            activity.group?.id,
            activity.created_at
          )
        ) {
          e.preventDefault()
          dispatch(
            toggleGroupModeratorOfferConfirmBox(
              activity?.group?.id?.toString?.()
            )
          )
        }
        break

      case 'group_transfer_request':
        // https://jira.nanameue.jp/browse/YAY-8006
        if (
          !isGroupActivityAlreadyAction(
            activities,
            ['group_ownership'],
            activity.group?.id,
            activity.created_at
          )
        ) {
          e.preventDefault()
          dispatch(
            toggleGroupOwnerTransferConfirmBox(
              activity?.group?.id?.toString?.()
            )
          )
        }
        break

      case 'group_walkthrough_request': {
        e.preventDefault()

        const _groupId = activity?.group?.id

        getGroupMutate({ groupId: _groupId?.toString?.() }).then((res) => {
          const _group = res?.['data']?.group as Group

          if (_group?.group_category_id == 4) {
            if (isNil(_group?.homepage)) {
              navigate(ROUTE_PATH.GROUP_WALKTHROUGH(_group.id))
            } else {
              navigate(ROUTE_PATH.GROUP_INFORMATION(_group.id))
            }
          } else {
            dispatch(
              toggleDefaultConfirmBox({
                title: t('confirm_box_walkthrough_unavailable.title'),
                content: '',
              })
            )
          }
        })

        break
      }

      case 'like': {
        e.preventDefault()

        const postId = activity?.to_post?.id?.toString?.()

        const res = await getPostMutate({
          postId,
        })

        const groupId = res?.['data']?.group?.id

        if (groupId) {
          const threadId = activity?.to_post?.thread_id

          if (threadId) {
            navigate(ROUTE_PATH.GROUP_THREAD(groupId, threadId))
          } else {
            navigate(ROUTE_PATH.GROUP_POST(groupId, postId))
          }
        } else {
          navigate(ROUTE_PATH.POST(postId))
        }

        break
      }

      case 'reply': {
        e.preventDefault()

        const res = await getPostMutate({
          postId: activity.from_post?.id.toString(),
        })

        const groupId = activity?.group?.id
        const conversationId = res?.['data']?.post?.conversation_id

        if (groupId) {
          const threadId = activity?.from_post?.thread_id

          if (threadId) {
            navigate(
              ROUTE_PATH.GROUP_THREAD_CONVERSATION(
                groupId,
                threadId,
                conversationId
              )
            )
          } else {
            navigate(ROUTE_PATH.GROUP_CONVERSATION(groupId, conversationId))
          }
        } else {
          navigate(
            conversationId
              ? ROUTE_PATH.CONVERSATION(conversationId)
              : ROUTE_PATH.POST(activity?.from_post?.id) // This should be never met condition.
          )
        }

        break
      }

      case 'post_move_to_thread': {
        e.preventDefault()

        navigate(
          ROUTE_PATH.GROUP_THREAD(
            activity?.group?.id,
            activity?.from_post?.thread_id
          )
        )

        break
      }

      default:
        break
    }
  }

  return (
    <Fragment>
      <div className="ActivitiesList">
        {activities
          .filter((activity) => {
            if (!isEmplGiftsEnabled) {
              return (
                activity.type !== 'review_gift' &&
                activity.type !== 'gift_received_user' &&
                activity.type !== 'gift_received_post' &&
                activity.type !== 'gift_received_group' &&
                activity.type !== 'gift_received_group_post'
              )
            } else {
              return true
            }
          })
          .map((activity) => (
            <ActivityListItem
              key={`${activity.user?.id}-${activity.created_at}`} // Activity does not return an id, so I use creativity
              activity={activity}
              onClick={onClick}
              pageType={pageType}
              enableViewportCheck={enableViewportCheck}
              resizeTrigger={resizeTrigger}
            />
          ))}
      </div>

      <ConfirmBoxBasic
        onClose={closeConfirmBox}
        onOk={onOk}
        isActive={isShowConfirmBox && !!confirmBoxContent}
        title={confirmBoxContent?.title}
        content={
          <span
            dangerouslySetInnerHTML={{
              __html: confirmBoxContent?.content,
            }}
          />
        }
        confirmButtonText={t('common.ok')}
      />
    </Fragment>
  )
}
